import {useState, useCallback, useEffect} from 'react';
import {List} from '@tickchak/react-share/app';
import VideoPopup from '../VideoPopup/VideoPopup';
import VideoFormPop from '../videoForm/videoFormPop';
import SupportVideosList from './SupportVideosList';
import SupportVideosFooter from './SupportVideosFooter';
import {usePopupsVisibility} from './hooks/usePopupsVisibility';
import {SupportVideosProps} from './SupportVideos.type';
import useRouteInfo from '../hooks/useRouteInfo';
import * as css from './supportVideos.css';
import VideoRatingPopup from '../VideoRatingPopup/VideoRatingPopup';
import {useSearchParams} from '../../../hooks/useSearchParams';
import {getSupportVideoById} from '../../../redux/management/managementActions';

function SupportVideos({openWidget, showVideos}: SupportVideosProps) {
    const [expandedVideoId, setExpandedVideoId] = useState(-1);
    
    const {
        popupsShowState,
        closeVideosList,
        openVideoDetails,
        closeVideoDetails,
        switchVideo,
        openVideoForm,
        closeVideoForm,
        openPopupRating,
        closePopupRating,
        openFormFromRating,
        openContactSupport
    } = usePopupsVisibility({showVideos, openWidget, setExpandedVideoId});
    const {pageType, routeType, id} = useRouteInfo();
    const isLoginPage = pageType === 'login' || pageType === 'signup';
    const searchParams = useSearchParams() as Record<string, string>;

    // Check for videoId query string and open video popup automatically
    useEffect(() => {
        const videoIdParam = searchParams.videoId;
        if (videoIdParam && !popupsShowState.videoDetails.isOpen) {
            const videoId = parseInt(videoIdParam);
            if (videoId && !isNaN(videoId)) {
                getSupportVideoById(videoId, routeType, id).then((video) => {
                    if (video) {
                        openVideoDetails(video, [], pageType);
                    }
                });
            }
        }
    }, [searchParams.videoId, routeType, id, pageType, openVideoDetails]);

    const showImage = typeof popupsShowState.videosList.showClass === 'string' 
        ? popupsShowState.videosList.showClass.includes('show') 
        : Boolean(popupsShowState.videosList.showClass);

    // Wrapper function to prevent closing list when clicking inside VideoPopup
    const handleCloseList = useCallback(async (event?: MouseEvent | React.MouseEvent) => {        
        // Check if VideoPopup is open and if the click was inside it
        if (popupsShowState.videoDetails.isOpen && event?.target) {
            const target = event.target as HTMLElement;
            // Check if the click was on an element inside VideoPopup
            const videoPopupElement = target.closest('[data-cy*="videoPopup"]');
            if (videoPopupElement) {
                return;
            }
        }
        // Close the list for all other cases
        await closeVideosList();
    }, [popupsShowState.videoDetails.isOpen, closeVideosList]);

    return (
        <css.wrapper 
            $showImage={showImage}
            $isLoginPage={isLoginPage}
        >
            {popupsShowState.videosList.isOpen && (
                <css.backdrop 
                    onClick={handleCloseList}
                    className={showImage ? 'show' : ''}
                />
            )}
            {popupsShowState.videosList.isOpen && (
                <List 
                    show={{...showVideos, close: handleCloseList}}
                    position={{
                        width: `${css.POPUP_WIDTH}px`, 
                        height: `${css.POPUP_HEIGHT}px`, 
                        bottom: css.POPUP_BOTTOM, 
                        inlineEnd: `${css.POPUP_INLINE_END}px`
                    }}
                    data-cy="support-videos-list"
                >
                    <SupportVideosList 
                        onOpenVideo={openVideoDetails}
                        onOpenForm={openVideoForm}
                        expandedVideoId={expandedVideoId}
                        onExpandedVideoIdChange={setExpandedVideoId}
                        onOpenRating={() => openPopupRating(pageType)}
                    />
                    <SupportVideosFooter 
                        onClose={showVideos.close}
                        onContactSupport={openContactSupport}
                        showFooter={expandedVideoId === -1}
                    />
                </List>
            )}
            
            {/* Video details popup */}
            {popupsShowState.videoDetails.isOpen && popupsShowState.videoDetails.data.video && (
                <VideoPopup 
                    video={popupsShowState.videoDetails.data.video}
                    onClose={closeVideoDetails}
                    openWidget={openContactSupport}
                    showClass={popupsShowState.videoDetails.showClass}
                    relatedVideos={popupsShowState.videoDetails.data.relatedVideos}
                    onSelectRelated={switchVideo}
                    onEditVideo={(videoId) => openFormFromRating(videoId)}
                />
            )}

            {popupsShowState.popupRating.isOpen && (
                <VideoRatingPopup
                    isOpen={popupsShowState.popupRating.isOpen}
                    showClass={popupsShowState.popupRating.showClass}
                    onClose={closePopupRating}
                    onEditVideo={(videoId, _pageType) => openFormFromRating(videoId)}
                />
            )}

            {/* Video form popup */}
            {popupsShowState.videoForm.isOpen && (
                <VideoFormPop 
                    videoId={popupsShowState.videoForm.data.videoId} 
                    show={{isOpen: popupsShowState.videoForm.isOpen, showClass: popupsShowState.videoForm.showClass, open: () => {}, close: closeVideoForm}} 
                    currentPage={popupsShowState.videoForm.data.pageType} 
                />
            )}
        </css.wrapper>
    );
}

export default SupportVideos;
